import { encodeMessage, type Message, Reassembler } from "./framing";

export type Unsubscribe = () => void;

/**
 * What a Channel needs from an RTCDataChannel. Relayed (iroh) streams are adapted to the same
 * shape (iroh-channel.ts), so everything above the transport works the same either way.
 */
export interface DataChannelLike {
  readonly label: string;
  readonly protocol: string;
  readonly readyState: "connecting" | "open" | "closing" | "closed";
  readonly bufferedAmount: number;
  bufferedAmountLowThreshold: number;
  binaryType: "blob" | "arraybuffer";
  onopen: ((event: Event) => void) | null;
  onclose: ((event: Event) => void) | null;
  /** `never`: handlers of any event type fit (RTCDataChannel's takes an RTCErrorEvent). */
  onerror: ((event: never) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  send(data: Uint8Array<ArrayBuffer>): void;
  close(): void;
  addEventListener(type: "bufferedamountlow", listener: () => void): void;
  removeEventListener(type: "bufferedamountlow", listener: () => void): void;
}

/** Messages held while nobody is listening yet (see #undelivered). */
const MAX_UNDELIVERED = 1_000;

/** Pause sending above this many buffered bytes; resume when it drains below the low mark. */
const HIGH_WATER_BYTES = 1024 * 1024;
const LOW_WATER_BYTES = 256 * 1024;

/**
 * A data channel with message framing (any size, text or binary), backpressure, and promise-based
 * sending. Can be created before its data channel exists (`attach` later); sends wait for open.
 */
export class Channel {
  #dc: DataChannelLike | undefined;
  #nextMessageId = 0;
  readonly #reassembler = new Reassembler();
  readonly #messageListeners = new Set<(message: Message) => void>();
  /**
   * Messages that arrived before anyone subscribed. A channel can open (and the peer start
   * talking) before the feature that consumes it attaches; dropping those would desync protocols
   * like Yjs sync, which never resend.
   */
  #undelivered: Message[] = [];
  readonly #closeListeners = new Set<() => void>();
  readonly #opened: Promise<void>;
  #resolveOpen!: () => void;
  #rejectOpen!: (error: Error) => void;
  #closed = false;
  /** Sends are serialized so frames of different messages never interleave on ordered channels. */
  #sending: Promise<void> = Promise.resolve();

  constructor(dc?: DataChannelLike) {
    this.#opened = new Promise((resolve, reject) => {
      this.#resolveOpen = resolve;
      this.#rejectOpen = reject;
    });
    this.#opened.catch(() => {}); // handled by callers of send()/opened
    if (dc) this.attach(dc);
  }

  get label(): string | undefined {
    return this.#dc?.label;
  }

  get protocol(): string | undefined {
    return this.#dc?.protocol;
  }

  /** Bytes queued for sending (backpressure). */
  get bufferedAmount(): number {
    return this.#dc?.bufferedAmount ?? 0;
  }

  get isOpen(): boolean {
    return this.#dc?.readyState === "open";
  }

  get closed(): boolean {
    return this.#closed;
  }

  /** Resolves when open; rejects if the channel closes first. */
  get opened(): Promise<void> {
    return this.#opened;
  }

  attach(dc: DataChannelLike): void {
    if (this.#dc) throw new Error("channel already attached");
    this.#dc = dc;
    dc.binaryType = "arraybuffer";
    dc.bufferedAmountLowThreshold = LOW_WATER_BYTES;
    dc.onopen = () => this.#resolveOpen();
    dc.onclose = () => this.#handleClose();
    dc.onerror = () => {
      if (dc.readyState === "closed") this.#handleClose();
    };
    dc.onmessage = (event: MessageEvent) => {
      const data = event.data;
      const frame =
        data instanceof ArrayBuffer
          ? new Uint8Array(data)
          : ArrayBuffer.isView(data)
            ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
            : undefined;
      if (!frame) return; // we only ever send binary frames
      const message = this.#reassembler.push(frame);
      if (message === undefined) return;
      if (this.#messageListeners.size === 0) {
        if (this.#undelivered.length < MAX_UNDELIVERED) this.#undelivered.push(message);
        return;
      }
      for (const listener of this.#messageListeners) listener(message);
    };
    if (dc.readyState === "open") this.#resolveOpen();
    if (dc.readyState === "closed") this.#handleClose();
  }

  /** Sends one message of any size. Resolves once it's handed to the browser's send buffer. */
  send(message: Message): Promise<void> {
    const sent = this.#sending.then(() => this.#sendNow(message));
    this.#sending = sent.catch(() => {});
    return sent;
  }

  /** Subscribes to messages. The first subscriber also receives anything that arrived earlier. */
  onMessage(listener: (message: Message) => void): Unsubscribe {
    this.#messageListeners.add(listener);
    if (this.#undelivered.length > 0) {
      const backlog = this.#undelivered;
      this.#undelivered = [];
      for (const message of backlog) listener(message);
    }
    return () => this.#messageListeners.delete(listener);
  }

  onClose(listener: () => void): Unsubscribe {
    if (this.#closed) {
      queueMicrotask(listener);
      return () => {};
    }
    this.#closeListeners.add(listener);
    return () => this.#closeListeners.delete(listener);
  }

  close(): void {
    this.#dc?.close();
    this.#handleClose();
  }

  async #sendNow(message: Message): Promise<void> {
    await this.#opened;
    const dc = this.#dc as DataChannelLike;
    for (const frame of encodeMessage(this.#nextMessageId++, message)) {
      if (dc.bufferedAmount > HIGH_WATER_BYTES) await this.#drained(dc);
      if (dc.readyState !== "open") throw new Error("channel closed");
      dc.send(frame as Uint8Array<ArrayBuffer>);
    }
  }

  #drained(dc: DataChannelLike): Promise<void> {
    return new Promise((resolve, reject) => {
      const done = () => {
        dc.removeEventListener("bufferedamountlow", done);
        this.#closeListeners.delete(fail);
        resolve();
      };
      const fail = () => {
        dc.removeEventListener("bufferedamountlow", done);
        reject(new Error("channel closed"));
      };
      dc.addEventListener("bufferedamountlow", done);
      this.#closeListeners.add(fail);
    });
  }

  #handleClose(): void {
    if (this.#closed) return;
    this.#closed = true;
    this.#rejectOpen(new Error("channel closed"));
    for (const listener of this.#closeListeners) listener();
    this.#closeListeners.clear();
  }
}
